import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowRight, BarChart2, CheckCircle2,
  ChevronDown, ChevronRight,
  Clock,
  Eye, EyeOff,
  FileText,
  GitBranch, Info, Lock,
  Minus, Network, Play,
  Send,
  TrendingDown,
  TrendingUp,
  Zap
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { EVENT_TYPES } from '../../hooks/useCommercialStream';
import { usePesa } from '../../stores/usePesaEngine';

// ─── Utilitaires ──────────────────────────────────────────────────────────────

const ytEmbed = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
  return m ? `https://www.youtube.com/embed/${m[1]}?autoplay=1&rel=0` : null;
};

const pick = (...vals) => vals.find(v => v !== undefined && v !== null && v !== '');

// Résout les prerequisites d'une action contre les actions complétées
const prereqsMet = (action, completedSet) => {
  if (!action?.prerequisites?.length) return true;
  return action.prerequisites.every(p => completedSet.has(p));
};

// Trouve la citation corpus pour une action
const findCorpusQuote = (actionId, scenario) => {
  const quotes = scenario?.initialState?.theory?.quotes || [];
  return quotes.find(q => q.actionId === actionId) || null;
};

// ─── Hook : nombre animé ──────────────────────────────────────────────────────

function useAnimatedNumber(target) {
  const [val, setVal] = useState(target);
  const ref = useRef(target);
  useEffect(() => {
    if (target === ref.current) return;
    const diff = target - ref.current;
    let i = 0;
    const id = setInterval(() => {
      i++;
      setVal(Math.round(ref.current + (diff * i) / 24));
      if (i >= 24) { clearInterval(id); ref.current = target; }
    }, 16);
    return () => clearInterval(id);
  }, [target]);
  return val;
}

// ─── Barre de progression du step ────────────────────────────────────────────

const StepTrack = ({ current, total, theme }) => (
  <div className="flex items-center gap-1.5">
    {Array.from({ length: total }).map((_, i) => (
      <motion.div
        key={i}
        animate={{ width: i === current ? '24px' : i < current ? '7px' : '5px', opacity: i > current ? 0.2 : 1 }}
        transition={{ duration: 0.3 }}
        className="h-[3px] rounded-full"
        style={{ background: i <= current ? theme.accent : `${theme.ink}12` }}
      />
    ))}
    <span className="ml-1 tabular-nums" style={{ color: `${theme.ink}30`, fontSize: '8px', fontFamily: '"DM Mono", monospace' }}>
      {current + 1}/{total}
    </span>
  </div>
);

// ─── Pill de phase ────────────────────────────────────────────────────────────

const PhasePill = ({ type, mode, theme }) => {
  const labels = {
    live: { observation: 'Contexte', action: 'Votre position', consequences: 'Résultats', tension: 'En cours', bifurcation: 'Analyse' },
    solo: { observation: 'Observation', action: 'Décision', consequences: 'Effets', tension: 'Tension', bifurcation: 'Bifurcation' },
  };
  const label = labels[mode === 'vente' ? 'live' : 'solo']?.[type] || type;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full select-none"
      style={{ background: `${theme.accent}12`, border: `1px solid ${theme.accent}28`, color: theme.accent, fontSize: '8px', fontFamily: '"DM Mono", monospace', letterSpacing: '0.18em', textTransform: 'uppercase' }}
    >
      <motion.span animate={{ opacity: [1, 0.3, 1] }} transition={{ duration: 2, repeat: Infinity }}
        className="w-1 h-1 rounded-full" style={{ background: theme.accent }} />
      {label}
    </span>
  );
};

// ─── Indicateur bayésien ──────────────────────────────────────────────────────
// Affiché en live comme donnée de marché réelle, pas comme décor.

const BayesianIndicator = ({ engine, theme, mode }) => {
  const proba = useMemo(() => {
    const p = engine?.state?.optimalPath?.probabilityOfSuccess
      ?? engine?.state?.prediction?.probability ?? 0;
    return Math.round(Math.max(0, Math.min(100, p > 1 ? p : p * 100)));
  }, [engine?.state]);

  const color = proba >= 70 ? '#10b981' : proba >= 45 ? theme.accent : '#ef4444';
  const label = mode === 'vente'
    ? (proba >= 70 ? 'Favorable' : proba >= 45 ? 'Incertain' : 'Difficile')
    : `Probabilité accord`;

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg"
        style={{ background: `${color}10`, border: `1px solid ${color}25` }}>
        <TrendingUp size={9} style={{ color }} />
        <span className="tabular-nums font-bold" style={{ color, fontSize: '11px', fontFamily: '"DM Mono", monospace', letterSpacing: '-0.02em' }}>
          {proba}%
        </span>
        <span style={{ color: `${color}70`, fontSize: '8px', fontFamily: '"DM Mono", monospace', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
          {label}
        </span>
      </div>
    </div>
  );
};

// ─── HUD ─────────────────────────────────────────────────────────────────────

const HUD = ({ currentStepIndex, totalSteps, currentPhase, displayTime, engine, theme, mode, isLiveMode }) => (
  <div className="flex items-center gap-3 flex-wrap select-none">
    <StepTrack current={currentStepIndex} total={totalSteps} theme={theme} />
    {currentPhase?.type && <PhasePill type={currentPhase.type} mode={mode} theme={theme} />}
    <div className="ml-auto flex items-center gap-2">
      <BayesianIndicator engine={engine} theme={theme} mode={mode} />
      {displayTime !== null && (
        <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg"
          style={{ background: `${theme.ink}06`, border: `1px solid ${theme.ink}10` }}>
          <Clock size={9} style={{ color: `${theme.ink}35` }} />
          <span style={{ color: `${theme.ink}45`, fontSize: '10px', fontFamily: '"DM Mono", monospace', fontWeight: 700 }}>
            {String(displayTime).padStart(2, '0')}s
          </span>
        </div>
      )}
    </div>
  </div>
);

// ─── Note vendeur (solo uniquement) ──────────────────────────────────────────

const SellerNote = ({ note, theme }) => {
  const [open, setOpen] = useState(false);
  if (!note) return null;
  return (
    <div className="relative">
      <button onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1 transition-opacity hover:opacity-70"
        style={{ color: `${theme.accent}35`, fontSize: '8px', fontFamily: '"DM Mono", monospace', letterSpacing: '0.18em', textTransform: 'uppercase' }}>
        <Eye size={8} /> Note vendeur
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 4, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.97 }} transition={{ duration: 0.13 }}
            className="absolute bottom-full left-0 mb-2 z-50 max-w-xs"
            style={{ background: theme.panelBg, border: `1px solid ${theme.accent}20`, borderRadius: '0.875rem', padding: '0.75rem 1rem', boxShadow: '0 8px 24px rgba(0,0,0,0.18)' }}>
            <p style={{ color: `${theme.ink}60`, fontSize: '11px', lineHeight: '1.55', fontFamily: '"DM Sans", sans-serif', fontStyle: 'italic' }}>
              {note}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── Bloc média ───────────────────────────────────────────────────────────────

const MediaBlock = ({ media, theme }) => {
  const [open, setOpen] = useState(false);
  if (!media?.url) return null;
  const embed = media.type === 'youtube' ? ytEmbed(media.url) : null;
  return (
    <AnimatePresence mode="wait">
      {!open ? (
        <motion.button key="t" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-2 mt-2 transition-all"
          style={{ color: `${theme.accent}50`, fontSize: '10px', fontFamily: '"DM Mono", monospace', letterSpacing: '0.12em', textTransform: 'uppercase' }}
          onMouseEnter={e => e.currentTarget.style.color = theme.accent}
          onMouseLeave={e => e.currentTarget.style.color = `${theme.accent}50`}>
          {media.type === 'youtube' ? <Play size={9} /> : <Eye size={9} />}
          {media.title || 'Voir'}
          <ChevronRight size={8} />
        </motion.button>
      ) : (
        <motion.div key="c" initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }} transition={{ duration: 0.18 }}
          className="mt-3 w-full rounded-2xl overflow-hidden"
          style={{ border: `1px solid ${theme.accent}18` }}>
          {embed
            ? <div className="aspect-video"><iframe src={embed} className="w-full h-full" allowFullScreen title={media.title} /></div>
            : <img src={media.url} alt={media.title || ''} className="w-full object-cover max-h-60" />
          }
          <button onClick={() => setOpen(false)}
            className="flex items-center justify-center gap-1 w-full py-2"
            style={{ background: `${theme.ink}04`, color: `${theme.ink}25`, borderTop: `1px solid ${theme.ink}06`, fontSize: '8px', fontFamily: '"DM Mono", monospace', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
            <EyeOff size={8} /> Réduire
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// ─── Chaîne causale (solo) ────────────────────────────────────────────────────

const CausalChainPanel = ({ chain, theme }) => {
  const [open, setOpen] = useState(false);
  if (!chain?.length) return null;
  return (
    <div>
      <button onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1.5 transition-opacity hover:opacity-70"
        style={{ color: `${theme.ink}30`, fontSize: '8px', fontFamily: '"DM Mono", monospace', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
        <Network size={8} />
        Chaîne causale
        {open ? <ChevronDown size={8} /> : <ChevronRight size={8} />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }} className="overflow-hidden mt-2 space-y-1.5">
            {chain.map((link, i) => (
              <div key={i} className="flex items-start gap-2 px-3 py-2 rounded-lg"
                style={{ background: `${theme.ink}03`, border: `1px solid ${theme.ink}06` }}>
                <div className="shrink-0 mt-1 w-1 h-1 rounded-full" style={{ background: `${theme.accent}50` }} />
                <div>
                  <p style={{ color: `${theme.ink}50`, fontSize: '10px', fontFamily: '"DM Sans", sans-serif' }}>
                    <span style={{ color: `${theme.ink}70` }}>{link.from}</span>
                    <span style={{ color: `${theme.ink}25`, margin: '0 4px' }}>→</span>
                    <span style={{ color: link.to?.includes('+') ? '#10b981' : link.to?.includes('-') ? '#ef4444' : `${theme.ink}70` }}>{link.to}</span>
                  </p>
                  {link.mechanism && (
                    <p style={{ color: `${theme.ink}28`, fontSize: '9px', fontStyle: 'italic', marginTop: '2px', fontFamily: '"DM Sans", sans-serif' }}>
                      {link.mechanism}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── Feedback profond (solo) ──────────────────────────────────────────────────

const DeepFeedbackPanel = ({ df, V, theme }) => {
  if (!df) return null;
  const items = [
    { key: 'whatHappened', color: `${theme.ink}60` },
    { key: 'whyItMatters', color: theme.accent },
    { key: 'whatItCosts',  color: '#ef4444' },
    { key: 'boostCondition', color: '#10b981' },
  ].filter(({ key }) => df[key]);

  if (!items.length) return null;
  return (
    <div className="mt-3 space-y-2">
      {items.map(({ key, color }) => (
        <div key={key} className="px-3 py-2.5 rounded-xl"
          style={{ background: `${theme.ink}03`, border: `1px solid ${theme.ink}06` }}>
          <p style={{ color: `${theme.ink}22`, fontSize: '7px', fontFamily: '"DM Mono", monospace', letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: '4px' }}>
            {V(key)?.label || key}
          </p>
          <p style={{ color, fontSize: '11px', lineHeight: '1.5', fontFamily: '"DM Sans", sans-serif' }}>
            {df[key]}
          </p>
        </div>
      ))}
    </div>
  );
};

// ─── Signal de compétence (solo) ──────────────────────────────────────────────

const CompetencyBadge = ({ signal, theme }) => {
  if (!signal?.name) return null;
  return (
    <div className="flex items-center gap-2 mt-2">
      <Zap size={9} style={{ color: `${theme.accent}50` }} />
      <span style={{ color: `${theme.accent}60`, fontSize: '9px', fontFamily: '"DM Mono", monospace', letterSpacing: '0.1em' }}>
        {signal.name} · {signal.level}
      </span>
    </div>
  );
};

// ─── Citation corpus (données de marché) ─────────────────────────────────────
// En live : affiché comme information de marché, sans attribution théâtrale.
// En solo : avec source complète.

const CorpusQuoteToast = (quote, theme, isLiveMode) => {
  if (!quote) return;
  toast.custom(() => (
    <motion.div
      initial={{ opacity: 0, y: isLiveMode ? 12 : 40 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className="max-w-sm w-full backdrop-blur-xl"
      style={{
        background: isLiveMode ? 'rgba(8,12,24,0.94)' : theme.panelBg,
        border: `1px solid ${theme.accent}20`,
        borderRadius: '1rem',
        padding: '0.875rem 1.125rem',
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
      }}
    >
      {!isLiveMode && (
        <p style={{ color: `${theme.accent}50`, fontSize: '7px', fontFamily: '"DM Mono", monospace', letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: '6px' }}>
          Corpus
        </p>
      )}
      <p style={{ color: isLiveMode ? 'rgba(245,220,150,0.88)' : `${theme.ink}65`, fontSize: '12px', lineHeight: '1.6', fontStyle: 'italic', fontFamily: '"DM Sans", sans-serif', textAlign: isLiveMode ? 'center' : 'left' }}>
        "{quote.text}"
      </p>
      {quote.author && (
        <p style={{ color: `${theme.ink}25`, fontSize: '9px', fontFamily: '"DM Mono", monospace', marginTop: '4px', textAlign: isLiveMode ? 'center' : 'left' }}>
          — {quote.author}
        </p>
      )}
    </motion.div>
  ), { duration: 5000, position: isLiveMode ? 'bottom-center' : 'bottom-right' });
};

// ─── Toast agent solo ─────────────────────────────────────────────────────────
// Solo seulement. Texte = conseil ou observation, jamais dialogue inventé.

const AgentSoloToast = (agent, text, theme) => {
  if (!text || text === '…' || !text.trim()) return;
  toast.custom(() => (
    <motion.div
      initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 30 }}
      className="max-w-xs w-full flex items-start gap-3 backdrop-blur-xl"
      style={{ background: theme.panelBg, border: `1px solid ${theme.panelBorder}`, borderRadius: '1rem', padding: '0.875rem', boxShadow: '0 8px 24px rgba(0,0,0,0.18)' }}>
      <div className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center"
        style={{ background: `${theme.accent}12`, border: `1px solid ${theme.accent}22` }}>
        <span style={{ color: theme.accent, fontSize: '11px', fontWeight: 700, fontFamily: '"DM Mono", monospace' }}>
          {(agent?.narrativeName || '?').charAt(0)}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <p style={{ color: `${theme.accent}60`, fontSize: '8px', fontFamily: '"DM Mono", monospace', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: '3px' }}>
          {agent?.narrativeName || 'Signal'}
        </p>
        <p style={{ color: `${theme.ink}65`, fontSize: '11px', lineHeight: '1.5', fontFamily: '"DM Sans", sans-serif' }}>
          {text}
        </p>
      </div>
    </motion.div>
  ), { duration: 4500, position: 'bottom-right' });
};

// ─── Carte d'action standard ──────────────────────────────────────────────────

const ActionCard = ({ action, onAction, disabled, isCompleted, isBlocked, isExclusiveDone, theme, mode, isLiveMode, V, index }) => {
  const [hov, setHov] = useState(false);
  const [showBlockReason, setShowBlockReason] = useState(false);

  const label = pick(isLiveMode ? action.clientLabel : null, action.label, 'Action');
  const weight = action.weight || 1;
  const blocked = isBlocked || isExclusiveDone;
  const inactive = disabled || blocked || isCompleted;

  // Couleur selon poids
  const weightColor = weight >= 3 ? theme.accent : weight === 2 ? `${theme.accent}80` : `${theme.ink}35`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: inactive ? (isCompleted ? 0.18 : 0.35) : 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.18 }}
      className="relative"
    >
      <button
        onClick={() => !inactive && onAction(action)}
        disabled={inactive}
        onMouseEnter={() => { setHov(true); if (isBlocked) setShowBlockReason(true); }}
        onMouseLeave={() => { setHov(false); setShowBlockReason(false); }}
        className="w-full text-left transition-all duration-150"
        style={{
          borderRadius: '1rem',
          padding: '1.1rem 1.4rem',
          background: isCompleted ? '#10b98108' : hov && !inactive ? `${theme.accent}07` : theme.panelBg,
          border: `1px solid ${isCompleted ? '#10b98120' : hov && !inactive ? `${theme.accent}22` : theme.panelBorder}`,
          cursor: inactive ? 'not-allowed' : 'pointer',
        }}
      >
        <div className="flex items-start gap-3">
          {/* Indicateur de poids */}
          <div className="shrink-0 mt-1 flex flex-col items-center gap-0.5">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="rounded-full transition-all"
                style={{ width: '3px', height: '3px', background: i < weight ? weightColor : `${theme.ink}12` }} />
            ))}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              {isBlocked && <Lock size={10} style={{ color: `${theme.ink}25`, flexShrink: 0 }} />}
              {isCompleted && <CheckCircle2 size={10} style={{ color: '#10b981', flexShrink: 0 }} />}
              <p style={{
                color: isBlocked ? `${theme.ink}25` : theme.ink,
                fontSize: weight >= 3 ? '14px' : '13px',
                fontWeight: weight >= 3 ? 500 : 400,
                fontFamily: '"DM Sans", sans-serif',
                letterSpacing: '-0.01em',
                lineHeight: '1.4',
              }}>
                {label}
              </p>
            </div>

            {/* Description courte (live uniquement si action a une description) */}
            {isLiveMode && action.description && hov && !inactive && (
              <motion.p initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                style={{ color: `${theme.ink}35`, fontSize: '11px', marginTop: '4px', fontFamily: '"DM Sans", sans-serif', lineHeight: '1.45', overflow: 'hidden' }}>
                {action.description}
              </motion.p>
            )}

            {/* Prérequis non remplis */}
            {isBlocked && action.prerequisites?.length > 0 && (
              <p style={{ color: `${theme.ink}22`, fontSize: '9px', marginTop: '3px', fontFamily: '"DM Mono", monospace', letterSpacing: '0.1em' }}>
                Requiert : {action.prerequisites.join(', ')}
              </p>
            )}

            {/* Média */}
            <MediaBlock media={action.media} theme={theme} />

            {/* Solo : signal de compétence */}
            {!isLiveMode && <CompetencyBadge signal={action.competencySignal} theme={theme} />}
          </div>

          {/* Flèche */}
          {!inactive && (
            <motion.div animate={{ x: hov ? 3 : 0 }} transition={{ duration: 0.12 }}
              className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center mt-0.5"
              style={{
                background: hov ? theme.accent : `${theme.ink}05`,
                border: `1px solid ${hov ? theme.accent : `${theme.ink}08`}`,
              }}>
              <ArrowRight size={12} style={{ color: hov ? '#fff' : `${theme.ink}22` }} />
            </motion.div>
          )}
        </div>
      </button>

      {/* Ligne de fond au hover */}
      {hov && !inactive && (
        <div className="absolute bottom-0 left-[20%] right-[20%] h-px"
          style={{ background: `linear-gradient(90deg, transparent, ${theme.accent}28, transparent)` }} />
      )}
    </motion.div>
  );
};

// ─── Groupe exclusif ──────────────────────────────────────────────────────────
// Un seul chemin possible. Une fois choisi, les autres s'effacent.

const ExclusiveGroup = ({ groupId, actions, onAction, completedSet, disabled, theme, mode, isLiveMode, V }) => {
  const chosen = actions.find(a => completedSet.has(a.id)) || null;
  const [hov, setHov] = useState(false);
  if (chosen) {
    const label = pick(isLiveMode ? chosen.clientLabel : null, chosen.label, 'Choix');
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        className="flex items-center gap-2.5 px-4 py-3 rounded-xl"
        style={{ background: '#10b98108', border: '1px solid #10b98120' }}>
        <CheckCircle2 size={13} style={{ color: '#10b981', flexShrink: 0 }} />
        <div>
          <p style={{ color: theme.ink, fontSize: '13px', fontFamily: '"DM Sans", sans-serif', fontWeight: 500 }}>{label}</p>
          <p style={{ color: `${theme.ink}30`, fontSize: '8px', fontFamily: '"DM Mono", monospace', letterSpacing: '0.15em', textTransform: 'uppercase', marginTop: '2px' }}>
            Voie choisie
          </p>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="space-y-1.5">
      {/* En-tête fork */}
      <div className="flex items-center gap-2 px-1">
        <GitBranch size={9} style={{ color: `${theme.ink}22` }} />
        <span style={{ color: `${theme.ink}22`, fontSize: '8px', fontFamily: '"DM Mono", monospace', letterSpacing: '0.18em', textTransform: 'uppercase' }}>
          Un seul choix
        </span>
        <div className="h-px flex-1" style={{ background: `${theme.ink}08` }} />
      </div>

      {actions.map((action, idx) => {
        const label = pick(isLiveMode ? action.clientLabel : null, action.label, 'Action');
        const isBlocked = !prereqsMet(action, completedSet);
        

        return (
          <div key={action.id}>
            <motion.button
              initial={{ opacity: 0, x: idx % 2 === 0 ? -4 : 4 }}
              animate={{ opacity: disabled || isBlocked ? 0.3 : 1, x: 0 }}
              transition={{ delay: idx * 0.06, duration: 0.16 }}
              whileHover={!disabled && !isBlocked ? { x: 2 } : {}}
              onClick={() => !disabled && !isBlocked && onAction(action)}
              disabled={disabled || isBlocked}
              onMouseEnter={() => setHov(true)}
              onMouseLeave={() => setHov(false)}
              className="w-full text-left transition-all duration-120"
              style={{
                borderRadius: '0.875rem',
                padding: '0.9rem 1.25rem',
                background: hov && !disabled && !isBlocked ? `${theme.accent}06` : `${theme.ink}03`,
                border: `1px solid ${hov && !disabled && !isBlocked ? `${theme.accent}20` : `${theme.ink}06`}`,
                cursor: disabled || isBlocked ? 'not-allowed' : 'pointer',
              }}
            >
              <div className="flex items-center gap-3">
                <div className="shrink-0 w-1.5 h-1.5 rounded-full transition-all"
                  style={{ background: hov && !disabled && !isBlocked ? theme.accent : `${theme.ink}18` }} />
                <p className="flex-1" style={{
                  color: isBlocked ? `${theme.ink}22` : theme.ink,
                  fontSize: (action.weight || 1) >= 3 ? '14px' : '13px',
                  fontWeight: (action.weight || 1) >= 3 ? 500 : 400,
                  fontFamily: '"DM Sans", sans-serif',
                  letterSpacing: '-0.01em',
                  lineHeight: '1.4',
                }}>
                  {label}
                </p>
                {isBlocked
                  ? <Lock size={10} style={{ color: `${theme.ink}18` }} />
                  : <ArrowRight size={11} style={{ color: hov ? theme.accent : `${theme.ink}18`, transition: 'color 0.12s' }} />
                }
              </div>

              {/* Description au hover */}
              <AnimatePresence>
                {hov && !disabled && !isBlocked && action.description && (
                  <motion.p initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.12 }}
                    className="overflow-hidden pl-5"
                    style={{ color: `${theme.ink}35`, fontSize: '11px', fontFamily: '"DM Sans", sans-serif', lineHeight: '1.45', marginTop: '5px' }}>
                    {action.description}
                  </motion.p>
                )}
              </AnimatePresence>
            </motion.button>

            {idx < actions.length - 1 && (
              <div className="flex items-center gap-3 py-0.5 px-4">
                <div className="h-px flex-1" style={{ background: `${theme.ink}05` }} />
                <span style={{ color: `${theme.ink}15`, fontSize: '8px', fontFamily: '"DM Mono", monospace' }}>ou</span>
                <div className="h-px flex-1" style={{ background: `${theme.ink}05` }} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ─── Post-action feedback ─────────────────────────────────────────────────────
// Affiché APRÈS qu'une action est complétée, dans la même zone.
// Live : effets métriques uniquement.
// Solo : effets + deepFeedback + causalChain + microEffects.

const PostActionFeedback = ({ actionId, step, engine, theme, isLiveMode, V }) => {
  const action = useMemo(
    () => step?.actions?.find(a => a.id === actionId),
    [step, actionId]
  );
  if (!action) return null;

  // Effets sur les paramètres
  const effects = Object.entries(action.effect || {}).filter(([, v]) => v !== 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.2 }}
      className="px-4 py-3 rounded-xl space-y-2"
      style={{ background: `${theme.ink}03`, border: `1px solid ${theme.ink}06` }}
    >
      {/* Effets métriques */}
      {effects.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {effects.slice(0, isLiveMode ? 3 : 5).map(([key, val]) => {
            const pos = val > 0;
            const color = pos ? '#10b981' : '#ef4444';
            return (
              <div key={key} className="flex items-center gap-1.5 px-2 py-1 rounded-lg"
                style={{ background: `${color}10`, border: `1px solid ${color}20` }}>
                {pos ? <TrendingUp size={9} style={{ color }} /> : <TrendingDown size={9} style={{ color }} />}
                <span style={{ color: `${theme.ink}55`, fontSize: '9px', fontFamily: '"DM Mono", monospace' }}>
                  {V(key)?.label || key}
                </span>
                <span style={{ color, fontSize: '10px', fontFamily: '"DM Mono", monospace', fontWeight: 700 }}>
                  {pos ? '+' : ''}{val}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Solo uniquement */}
      {!isLiveMode && (
        <>
          <DeepFeedbackPanel df={action.deepFeedback} V={V} theme={theme} />
          <CausalChainPanel chain={action.causalChain} theme={theme} />
          {action.microEffects?.length > 0 && (
            <div className="pt-1">
              <p style={{ color: `${theme.ink}22`, fontSize: '8px', fontFamily: '"DM Mono", monospace', letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: '4px' }}>
                Effets individuels
              </p>
              <div className="space-y-1">
                {action.microEffects.slice(0, 3).map((me, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span style={{ color: `${theme.ink}35`, fontSize: '9px', fontFamily: '"DM Mono", monospace' }}>{me.target}</span>
                    <span style={{ color: `${theme.ink}18` }}>·</span>
                    <span style={{ color: `${theme.ink}35`, fontSize: '9px', fontFamily: '"DM Mono", monospace' }}>{me.variable}</span>
                    <span style={{ color: me.delta > 0 ? '#10b981' : '#ef4444', fontSize: '9px', fontFamily: '"DM Mono", monospace', fontWeight: 700 }}>
                      {me.delta > 0 ? '+' : ''}{me.delta}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </motion.div>
  );
};

// ─── Scène narrative ──────────────────────────────────────────────────────────
// Live : clientNarrative + clientQuestion seulement.
// Solo : narrative complète + theoreticalHook + sellerNote.

const NarrativeScene = ({ phase, step, theme, mode, isLiveMode, isConsequence }) => {
  // Texte principal : live = clientNarrative en priorité, solo = narrative
  const text = pick(
    isLiveMode ? phase?.clientNarrative : null,
    phase?.narrative,
    isLiveMode ? null : step?.narrativeOverride,
    isLiveMode ? null : step?.description,
    ''
  );

  // Question : live = clientQuestion, solo = problematisation
  const question = isLiveMode
    ? (phase?.clientQuestion || null)
    : (step?.problematisation || null);

  // Hook théorique (solo uniquement)
  const hook = !isLiveMode ? (phase?.theoreticalHook || step?.epistemologicalQuestion || null) : null;

  const sellerNote = !isLiveMode ? phase?.sellerNote : null;

  if (!text && !question) return null;

  return (
    <motion.div
      key={`narr-${phase?.title}`}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.32, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="w-full space-y-3"
    >
      {/* Texte principal */}
      {text && (
        <div className="w-full relative"
          style={{
            background: isConsequence ? `${theme.accent}05` : theme.panelBg,
            border: `1px solid ${isConsequence ? `${theme.accent}15` : theme.panelBorder}`,
            borderRadius: '1.75rem',
            padding: '2rem 2.5rem',
          }}
        >
          {/* Ligne décorative */}
          <div className="absolute top-0 left-[20%] right-[20%] h-px"
            style={{ background: `linear-gradient(90deg, transparent, ${theme.accent}25, transparent)` }} />

          <p style={{
            color: theme.ink,
            fontSize: isLiveMode ? '18px' : '16px',
            fontWeight: 300,
            lineHeight: '1.65',
            fontFamily: '"DM Sans", sans-serif',
            letterSpacing: '-0.015em',
            textAlign: 'center',
          }}>
            {text}
          </p>

          {/* Hook (solo) */}
          {hook && (
            <div className="mt-4 pt-3" style={{ borderTop: `1px solid ${theme.ink}07` }}>
              <p style={{ color: `${theme.ink}35`, fontSize: '11px', lineHeight: '1.55', fontStyle: 'italic', fontFamily: '"DM Sans", sans-serif', textAlign: 'center' }}>
                {hook}
              </p>
            </div>
          )}

          {/* Note vendeur (solo) */}
          {sellerNote && (
            <div className="mt-3 pt-2" style={{ borderTop: `1px solid ${theme.ink}05` }}>
              <SellerNote note={sellerNote} theme={theme} />
            </div>
          )}
        </div>
      )}

      {/* Question */}
      {question && (
        <motion.div
          initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
          className="px-5 py-3.5 rounded-2xl text-center"
          style={{ background: `${theme.accent}06`, border: `1px solid ${theme.accent}10` }}
        >
          <p style={{
            color: theme.ink,
            fontSize: isLiveMode ? '15px' : '13px',
            fontWeight: 300,
            fontStyle: isLiveMode ? 'italic' : 'normal',
            lineHeight: '1.55',
            fontFamily: '"DM Sans", sans-serif',
            letterSpacing: '-0.01em',
          }}>
            {question}
          </p>
        </motion.div>
      )}
    </motion.div>
  );
};

// ─── Scène d'action ───────────────────────────────────────────────────────────

const ActionScene = ({
  phase, step, availableActions, completedActions,
  isConstraintReached, onAction, onAdvance,
  activeMedia, onClearMedia,
  lastCompletedActionId,
  theme, mode, isLiveMode, V,
  keyActionDone, feedbackIfMissed,
  engine,
}) => {
  const completedSet = useMemo(
    () => completedActions instanceof Set ? completedActions : new Set(completedActions || []),
    [completedActions]
  );

  // Grouper par exclusiveGroup
  const { groups, standalone } = useMemo(() => {
    const gmap = {};
    const solo = [];
    availableActions.forEach(a => {
      if (a.exclusiveGroup) {
        if (!gmap[a.exclusiveGroup]) gmap[a.exclusiveGroup] = [];
        gmap[a.exclusiveGroup].push(a);
      } else {
        solo.push(a);
      }
    });
    return { groups: gmap, standalone: solo };
  }, [availableActions]);

  const groupDone = (gActions) => gActions.some(a => completedSet.has(a.id));

  const advanceLabel = pick(phase?.advanceLabel, isLiveMode ? 'Continuer' : 'Passer à la suite');

  // Texte d'ouverture de la phase action (question ou problématisation)
  const openingText = pick(
    isLiveMode ? phase?.clientQuestion : null,
    !isLiveMode ? step?.problematisation : null,
    null
  );

  return (
    <motion.div
      key="action-scene"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.22 }}
      className="w-full space-y-3 pointer-events-auto"
    >
      {/* Média actif */}
      <AnimatePresence>
        {activeMedia && (
          <motion.div key="media" initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="w-full rounded-2xl overflow-hidden"
            style={{ border: `1px solid ${theme.accent}18` }}>
            {/* ✅ Changement ici : utiliser activeMedia.type et activeMedia.url directement */}
            {activeMedia.type === 'youtube' && ytEmbed(activeMedia.url)
              ? <div className="aspect-video"><iframe src={ytEmbed(activeMedia.url)} className="w-full h-full" allowFullScreen title={activeMedia.title || ''} /></div>
              : <img src={activeMedia.url} alt={activeMedia.title || ''} className="w-full object-cover max-h-60" />
            }
            <button onClick={onClearMedia}
              className="flex items-center justify-center gap-1 w-full py-2"
              style={{ background: `${theme.ink}04`, color: `${theme.ink}22`, borderTop: `1px solid ${theme.ink}06`, fontSize: '8px', fontFamily: '"DM Mono", monospace', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
              <Minus size={8} /> Réduire
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Texte d'ouverture */}
      {openingText && (
        <div className="px-5 py-3.5 rounded-2xl text-center"
          style={{ background: theme.panelBg, border: `1px solid ${theme.accent}10` }}>
          <p style={{ color: theme.ink, fontSize: isLiveMode ? '16px' : '14px', fontWeight: 300, fontStyle: 'italic', lineHeight: '1.5', fontFamily: '"DM Sans", sans-serif' }}>
            {openingText}
          </p>
        </div>
      )}

      {/* Post-action feedback (dernière action complétée) */}
      {lastCompletedActionId && (
        <PostActionFeedback
          actionId={lastCompletedActionId}
          step={step}
          engine={engine}
          theme={theme}
          isLiveMode={isLiveMode}
          V={V}
        />
      )}

      {/* Groupes exclusifs */}
      {Object.entries(groups).map(([groupId, groupActions]) => (
        <ExclusiveGroup
          key={groupId}
          groupId={groupId}
          actions={groupActions}
          onAction={onAction}
          completedSet={completedSet}
          disabled={isConstraintReached || groupDone(groupActions)}
          theme={theme}
          mode={mode}
          isLiveMode={isLiveMode}
          V={V}
        />
      ))}

      {/* Séparateur si mixte */}
      {Object.keys(groups).length > 0 && standalone.length > 0 && (
        <div className="flex items-center gap-3 px-1">
          <div className="h-px flex-1" style={{ background: `${theme.ink}06` }} />
          <span style={{ color: `${theme.ink}16`, fontSize: '8px', fontFamily: '"DM Mono", monospace', letterSpacing: '0.18em', textTransform: 'uppercase' }}>
            aussi disponible
          </span>
          <div className="h-px flex-1" style={{ background: `${theme.ink}06` }} />
        </div>
      )}

      {/* Actions standalone */}
      <div className="space-y-2">
        {standalone.map((action, idx) => (
          <ActionCard
            key={action.id}
            action={action}
            index={idx}
            disabled={isConstraintReached}
            isCompleted={completedSet.has(action.id)}
            isBlocked={!prereqsMet(action, completedSet)}
            isExclusiveDone={false}
            onAction={onAction}
            theme={theme}
            mode={mode}
            isLiveMode={isLiveMode}
            V={V}
          />
        ))}
      </div>

      {availableActions.length === 0 && (
        <div className="py-6 rounded-2xl text-center"
          style={{ background: `${theme.ink}02`, border: `1px dashed ${theme.ink}07` }}>
          <p style={{ color: `${theme.ink}18`, fontSize: '11px', fontFamily: '"DM Mono", monospace' }}>En attente…</p>
        </div>
      )}

      {/* Hint formation (solo) */}
      {!isLiveMode && !keyActionDone && feedbackIfMissed && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
          className="flex items-start gap-2 px-4 py-3 rounded-xl"
          style={{ background: `${theme.accent}06`, border: `1px solid ${theme.accent}12` }}>
          <Info size={11} style={{ color: `${theme.accent}50`, flexShrink: 0, marginTop: '1px' }} />
          <p style={{ color: `${theme.accent}65`, fontSize: '11px', lineHeight: '1.5', fontFamily: '"DM Sans", sans-serif', fontStyle: 'italic' }}>
            {feedbackIfMissed}
          </p>
        </motion.div>
      )}

      {/* Bouton continuer */}
      <motion.button
        initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}
        whileHover={{ y: -1 }} whileTap={{ scale: 0.989 }}
        onClick={onAdvance}
        className="w-full py-3.5 rounded-2xl font-semibold flex items-center justify-center gap-3"
        style={{
          background: theme.accent,
          color: '#fff',
          fontSize: '14px',
          fontFamily: '"DM Sans", sans-serif',
          letterSpacing: '-0.01em',
          boxShadow: `0 4px 18px ${theme.accent}28`,
        }}
        onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
        onMouseLeave={e => e.currentTarget.style.opacity = '1'}
      >
        {advanceLabel}
        <ArrowRight size={14} />
      </motion.button>
    </motion.div>
  );
};

const ClientActionScene = ({ clientActions = [], theme, postAction, phaseKey }) => {
  const [responses,    setResponses]    = useState({});
  const [submitted,    setSubmitted]    = useState({});
  const [textInputs,   setTextInputs]   = useState({});
 
  const handleChoice = async (action, option) => {
    if (submitted[action.id]) return; // une seule réponse par action
 
    const event = {
      type:     EVENT_TYPES.CLIENT_CHOICE,
      id:       action.id,
      prompt:   action.prompt,
      choiceId: option.id,
      label:    option.label,
      effect:   option.effect || {},
      phaseKey,
    };
 
    setResponses(prev => ({ ...prev, [action.id]: option }));
    setSubmitted(prev => ({ ...prev, [action.id]: true }));
 
    await postAction(event);
  };
 
  const handleInput = async (action) => {
    const text = textInputs[action.id]?.trim();
    if (!text || submitted[action.id]) return;
 
    const event = {
      type:       EVENT_TYPES.CLIENT_INPUT,
      id:         action.id,
      prompt:     action.prompt,
      fieldLabel: action.fieldLabel,
      text,
      phaseKey,
    };
 
    setSubmitted(prev => ({ ...prev, [action.id]: true }));
    await postAction(event);
  };
 
  if (!clientActions.length) return null;
 
  const g = {
    textPrimary: theme.isDark !== false ? 'rgba(255,255,255,0.9)' : theme.ink,
    textSec:     theme.isDark !== false ? 'rgba(255,255,255,0.5)' : `${theme.ink}80`,
    rowBg:       theme.isDark !== false ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
    rowBorder:   theme.isDark !== false ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.08)',
  };
 
  return (
    <div className="w-full space-y-4">
      {clientActions.map((action) => (
        <div key={action.id} className="space-y-2">
          {/* Prompt */}
          <p style={{
            color: g.textSec,
            fontSize: '12px',
            fontFamily: '"DM Mono", monospace',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
          }}>
            {action.prompt}
          </p>
 
          {/* Choix */}
          {action.type === 'choice' && (
            <div className="space-y-2">
              {action.options.map((option) => {
                const isChosen  = responses[action.id]?.id === option.id;
                const isLocked  = !!submitted[action.id];
                const isOther   = isLocked && !isChosen;
 
                return (
                  <motion.button
                    key={option.id}
                    initial={{ opacity: 0, x: -6 }}
                    animate={{ opacity: isOther ? 0.2 : 1, x: 0 }}
                    onClick={() => handleChoice(action, option)}
                    disabled={isLocked}
                    className="w-full text-left px-4 py-3 rounded-2xl transition-all"
                    style={{
                      background: isChosen
                        ? `${theme.accent}12`
                        : g.rowBg,
                      border: `1px solid ${isChosen ? `${theme.accent}35` : g.rowBorder}`,
                      cursor: isLocked ? 'default' : 'pointer',
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <motion.div
                        animate={{ background: isChosen ? theme.accent : g.rowBorder }}
                        className="w-1.5 h-1.5 rounded-full shrink-0"
                      />
                      <p style={{
                        color: isChosen ? theme.accent : g.textPrimary,
                        fontSize: '13px',
                        fontFamily: '"DM Sans", sans-serif',
                        fontWeight: isChosen ? 500 : 400,
                      }}>
                        {option.label}
                      </p>
                      {isChosen && (
                        <span style={{
                          marginLeft: 'auto',
                          color: theme.accent,
                          fontSize: '8px',
                          fontFamily: '"DM Mono", monospace',
                          letterSpacing: '0.18em',
                          textTransform: 'uppercase',
                        }}>
                          Envoyé
                        </span>
                      )}
                    </div>
                  </motion.button>
                );
              })}
            </div>
          )}
 
          {/* Saisie libre */}
          {action.type === 'input' && (
            <div className="space-y-2">
              <div className="relative">
                <textarea
                  value={textInputs[action.id] || ''}
                  onChange={e => setTextInputs(prev => ({ ...prev, [action.id]: e.target.value }))}
                  disabled={submitted[action.id]}
                  placeholder={action.fieldLabel || 'Votre réponse…'}
                  rows={3}
                  className="w-full px-4 py-3 rounded-2xl resize-none outline-none transition-all"
                  style={{
                    background:  g.rowBg,
                    border:      `1px solid ${submitted[action.id] ? `${theme.accent}35` : g.rowBorder}`,
                    color:       g.textPrimary,
                    fontSize:    '13px',
                    fontFamily:  '"DM Sans", sans-serif',
                    lineHeight:  '1.5',
                  }}
                />
              </div>
              {!submitted[action.id] && (
                <motion.button
                  whileHover={{ y: -1 }}
                  whileTap={{ scale: 0.987 }}
                  onClick={() => handleInput(action)}
                  disabled={!textInputs[action.id]?.trim()}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl"
                  style={{
                    background: textInputs[action.id]?.trim()
                      ? `linear-gradient(135deg, ${theme.accent}, ${theme.accent}cc)`
                      : g.rowBg,
                    color: textInputs[action.id]?.trim() ? '#fff' : g.textSec,
                    border: `1px solid ${textInputs[action.id]?.trim() ? theme.accent : g.rowBorder}`,
                    fontSize: '12px',
                    fontFamily: '"DM Mono", monospace',
                    letterSpacing: '0.12em',
                    cursor: textInputs[action.id]?.trim() ? 'pointer' : 'not-allowed',
                  }}
                >
                  <Send size={11} />
                  Envoyer
                </motion.button>
              )}
              {submitted[action.id] && (
                <p style={{ color: theme.accent, fontSize: '9px', fontFamily: '"DM Mono", monospace', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
                  Reçu — merci
                </p>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const ClientActivity = ({ theme }) => {
  const compiledClientEvents = usePesa(s => s.commercialLive?.compiledClientEvents);
  
  const clientChoices = compiledClientEvents?.filter(e => e.type === EVENT_TYPES.CLIENT_CHOICE) ?? [];
  const clientInputs  = compiledClientEvents?.filter(e => e.type === EVENT_TYPES.CLIENT_INPUT)  ?? [];
  
  const isEmpty = clientChoices.length === 0 && clientInputs.length === 0;
  if (isEmpty) return null;

  return (
    <div className="mt-4 p-4 rounded-xl space-y-3" style={{ background: `${theme.accent}08`, border: `1px solid ${theme.accent}20` }}>
      <div className="flex items-center gap-2 mb-2">
        <Eye size={14} style={{ color: theme.accent }} />
        <span className="text-xs font-mono tracking-widest uppercase" style={{ color: theme.accent }}>
          Activité client
        </span>
      </div>

      {/* Choix */}
      {clientChoices?.slice(-3).reverse().map((choice, idx) => (
        <div key={choice.id || idx} className="flex items-start gap-2 text-xs" style={{ color: `${theme.ink}70` }}>
          <CheckCircle2 size={12} className="mt-0.5 shrink-0" style={{ color: '#10b981' }} />
          <div>
            <span className="font-semibold">{choice.label}</span>
            {choice.prompt && <span className="opacity-50 ml-1">— {choice.prompt}</span>}
          </div>
        </div>
      ))}

      {/* Saisies */}
      {clientInputs?.slice(-3).reverse().map((input, idx) => (
        <div key={input.id || idx} className="flex items-start gap-2 text-xs" style={{ color: `${theme.ink}70` }}>
          <FileText size={12} className="mt-0.5 shrink-0" style={{ color: theme.accent }} />
          <div>
            <span className="italic">"{input.text}"</span>
            {input.prompt && <span className="opacity-50 ml-1">— {input.prompt}</span>}
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Impact reveal (conséquences) ────────────────────────────────────────────

const AnimNum = ({ value, style }) => {
  const v = useAnimatedNumber(value);
  return <span style={style}>{v}</span>;
};

const ImpactReveal = ({ deltas, theme, isLiveMode, V }) => {
  const items = Object.entries(deltas)
    .filter(([, v]) => v.delta !== 0)
    .sort((a, b) => Math.abs(b[1].delta) - Math.abs(a[1].delta))
    .slice(0, isLiveMode ? 3 : 5);

  if (!items.length) return null;
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.15 }}>
      <div className="px-4 py-4 rounded-2xl"
        style={{ background: theme.panelBg, border: `1px solid ${theme.panelBorder}` }}>
        <div className="flex items-center gap-2 mb-3">
          <BarChart2 size={10} style={{ color: theme.accent }} />
          <span style={{ color: `${theme.ink}30`, fontSize: '8px', fontFamily: '"DM Mono", monospace', letterSpacing: '0.2em', textTransform: 'uppercase' }}>
            {isLiveMode ? 'Impact de vos choix' : 'Indicateurs mis à jour'}
          </span>
        </div>
        <div className={`grid gap-2 ${isLiveMode ? 'grid-cols-3' : 'grid-cols-2 sm:grid-cols-3'}`}>
          {items.map(([key, { after, delta }]) => {
            const pos = delta > 0;
            const color = pos ? '#10b981' : '#f87171';
            return (
              <div key={key} className="p-3 rounded-xl"
                style={{ background: `${theme.ink}03`, border: `1px solid ${theme.ink}06` }}>
                <p style={{ color: `${theme.ink}28`, fontSize: '8px', fontFamily: '"DM Mono", monospace', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '5px' }}>
                  {V?.(key)?.label || key}
                </p>
                <div className="flex items-end gap-1.5 mb-1.5">
                  <AnimNum value={after}
                    style={{ fontSize: '1.25rem', fontWeight: 700, fontFamily: '"DM Mono", monospace', color: theme.ink, letterSpacing: '-0.04em' }} />
                  <span style={{ color, fontSize: '10px', fontWeight: 700, fontFamily: '"DM Mono", monospace', paddingBottom: '2px' }}>
                    {pos ? '+' : ''}{delta}
                  </span>
                </div>
                <div className="h-[2px] rounded-full overflow-hidden" style={{ background: `${theme.ink}08` }}>
                  <motion.div className="h-full rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.min(100, Math.max(0, after))}%` }}
                    transition={{ duration: 0.9, ease: [0.25, 0.46, 0.45, 0.94] }}
                    style={{ background: color }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
};

// ─── Débrief final ────────────────────────────────────────────────────────────

const DebriefScene = ({ step, scenario, completedActions, theme, isLiveMode, V, commercialLiveGetDebriefData }) => {
  const safeCompleted = completedActions instanceof Set ? [...completedActions] : (completedActions || []);
  const debriefData = commercialLiveGetDebriefData?.();
  const matched = useMemo(() => {
    if (!scenario?.solutions?.length) return null;
    return scenario.solutions.reduce((best, sol) => {
      const req   = sol.requiredActions || [];
      const score = req.length > 0 ? req.filter(a => safeCompleted.includes(a)).length / req.length : 0;
      return score > (best?.score || -1) ? { ...sol, score } : best;
    }, null);
  }, [scenario?.solutions, safeCompleted]);

  const tone      = matched?.interpretation?.tone || 'neutre';
  const toneColor = { radieux: '#10b981', lucide: theme.accent, sombre: '#ef4444', cynique: '#94a3b8', tragique: '#ef4444', désespéré: '#ef4444' }[tone] || theme.accent;

  // Live : résumé court + keyInsight
  // Solo : tout

  return (
    <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="w-full space-y-3">

      {/* Carte principale */}
      <div className="relative w-full"
        style={{ background: theme.panelBg, border: `1px solid ${toneColor}22`, borderRadius: '1.75rem', padding: '2rem 2.5rem', boxShadow: `0 0 0 1px ${toneColor}08, 0 12px 48px ${toneColor}06` }}>
        <div className="absolute top-0 left-[12%] right-[12%] h-px"
          style={{ background: `linear-gradient(90deg, transparent, ${toneColor}40, transparent)` }} />

        {matched?.name && (
          <p className="text-center mb-3"
            style={{ color: `${toneColor}55`, fontSize: '8px', fontFamily: '"DM Mono", monospace', letterSpacing: '0.25em', textTransform: 'uppercase' }}>
            {matched.name}
          </p>
        )}

        <p className="text-center"
          style={{ color: theme.ink, fontSize: isLiveMode ? '19px' : '17px', fontWeight: 300, lineHeight: '1.6', fontFamily: '"DM Sans", sans-serif', letterSpacing: '-0.015em' }}>
          {pick(matched?.interpretation?.shortSummary, 'Parcours terminé.')}
        </p>

        

        {matched?.interpretation?.keyInsight && (
          <div className="mt-4 pt-3" style={{ borderTop: `1px solid ${theme.ink}07` }}>
            <p className="text-center"
              style={{ color: `${theme.ink}42`, fontSize: '12px', lineHeight: '1.55', fontStyle: 'italic', fontFamily: '"DM Sans", sans-serif' }}>
              {matched.interpretation.keyInsight}
            </p>
          </div>
        )}
      </div>

      {/* Données live compilées */}
      {debriefData && (
        <div className="px-4 py-3 rounded-xl" style={{ background: `${theme.accent}06`, border: `1px solid ${theme.accent}15` }}>
          <p style={{ color: `${theme.accent}50`, fontSize: '8px', fontFamily: '"DM Mono", monospace', letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: '6px' }}>
            Synthèse de vos échanges
          </p>
          <pre style={{ color: `${theme.ink}50`, fontSize: '11px', fontFamily: '"DM Mono", monospace', whiteSpace: 'pre-wrap', margin: 0 }}>
            {JSON.stringify(debriefData, null, 2)}
          </pre>
        </div>
      )}

      {/* Probabilités */}
      {matched?.interpretation && (
        <div className="grid grid-cols-2 gap-3">
          <div className="px-4 py-3 rounded-xl text-center"
            style={{ background: `${theme.ink}03`, border: `1px solid ${theme.ink}06` }}>
            <p style={{ color: `${theme.ink}22`, fontSize: '8px', fontFamily: '"DM Mono", monospace', letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: '4px' }}>
              Probabilité théorique
            </p>
            <span style={{ color: theme.ink, fontSize: '1.5rem', fontWeight: 700, fontFamily: '"DM Mono", monospace' }}>
              {matched.interpretation.theoreticalPromise ?? 0}%
            </span>
          </div>
          <div className="px-4 py-3 rounded-xl text-center"
            style={{ background: `${toneColor}08`, border: `1px solid ${toneColor}20` }}>
            <p style={{ color: `${toneColor}60`, fontSize: '8px', fontFamily: '"DM Mono", monospace', letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: '4px' }}>
              Probabilité réelle
            </p>
            <span style={{ color: toneColor, fontSize: '1.5rem', fontWeight: 700, fontFamily: '"DM Mono", monospace' }}>
              {matched.interpretation.realWorldProbability ?? 0}%
            </span>
          </div>
        </div>
      )}

      {/* Contrefactuels (solo uniquement) */}
      {!isLiveMode && scenario?.counterfactuals?.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className="space-y-1.5">
          <p style={{ color: `${theme.ink}22`, fontSize: '8px', fontFamily: '"DM Mono", monospace', letterSpacing: '0.2em', textTransform: 'uppercase', paddingLeft: '2px' }}>
            Contrefactuels (chemins alternatifs)
          </p>
          {scenario.counterfactuals.slice(0, 4).map((cf, i) => (
            <motion.div key={i} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.35 + i * 0.07 }}
              className="flex items-start gap-3 px-4 py-3 rounded-xl"
              style={{ background: `${theme.ink}03`, border: `1px solid ${theme.ink}06` }}>
              <div className="shrink-0 mt-1 w-1.5 h-1.5 rounded-full" style={{ background: `${theme.accent}50` }} />
              <div>
                <p style={{ color: theme.ink, fontSize: '12px', fontWeight: 500, fontFamily: '"DM Sans", sans-serif' }}>{cf.actionTitle}</p>
                <p style={{ color: `${theme.ink}35`, fontSize: '10px', marginTop: '2px', fontFamily: '"DM Sans", sans-serif', fontStyle: 'italic', lineHeight: '1.4' }}>{cf.description}</p>
                <span className="inline-block mt-1 px-2 py-0.5 rounded-md text-[8px] font-mono" style={{ background: `${theme.accent}10`, color: theme.accent, border: `1px solid ${theme.accent}20` }}>
                  +{(cf.deltaProbability * 100).toFixed(0)}% probabilité
                </span>
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Message de débrief personnalisé (depuis le template) */}
      {step?.debriefMessage && (
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
          className="text-center"
          style={{ color: `${theme.ink}40`, fontSize: '12px', lineHeight: '1.6', fontFamily: '"DM Sans", sans-serif', fontStyle: 'italic' }}>
          {step.debriefMessage}
        </motion.p>
      )}

      {/* Compétences (solo) */}
      {!isLiveMode && matched?.interpretation?.competencies?.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
          className="space-y-1.5">
          <p style={{ color: `${theme.ink}22`, fontSize: '8px', fontFamily: '"DM Mono", monospace', letterSpacing: '0.2em', textTransform: 'uppercase', paddingLeft: '2px' }}>
            Compétences mobilisées
          </p>
          {matched.interpretation.competencies.map((c, i) => (
            <motion.div key={i} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 + i * 0.07 }}
              className="flex items-start justify-between gap-3 px-4 py-3 rounded-xl"
              style={{ background: `${theme.ink}03`, border: `1px solid ${theme.ink}06` }}>
              <div>
                <p style={{ color: theme.ink, fontSize: '12px', fontWeight: 500, fontFamily: '"DM Sans", sans-serif' }}>{c.name}</p>
                {c.evidence && <p style={{ color: `${theme.ink}32`, fontSize: '10px', marginTop: '2px', fontFamily: '"DM Sans", sans-serif', fontStyle: 'italic', lineHeight: '1.4' }}>{c.evidence}</p>}
              </div>
              <span className="shrink-0 px-2 py-0.5 rounded-md"
                style={{ background: `${toneColor}10`, color: toneColor, fontSize: '8px', fontFamily: '"DM Mono", monospace', letterSpacing: '0.1em', textTransform: 'uppercase', border: `1px solid ${toneColor}18` }}>
                {c.level}
              </span>
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Questions ouvertes (solo) */}
      {!isLiveMode && matched?.interpretation?.openQuestions?.length > 0 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
          className="px-4 py-3 rounded-xl space-y-2"
          style={{ background: `${theme.accent}05`, border: `1px solid ${theme.accent}10` }}>
          <p style={{ color: `${theme.accent}50`, fontSize: '8px', fontFamily: '"DM Mono", monospace', letterSpacing: '0.2em', textTransform: 'uppercase' }}>
            Questions ouvertes
          </p>
          {matched.interpretation.openQuestions.slice(0, 2).map((q, i) => (
            <p key={i} style={{ color: `${theme.ink}45`, fontSize: '11px', lineHeight: '1.55', fontFamily: '"DM Sans", sans-serif', fontStyle: 'italic' }}>
              {q}
            </p>
          ))}
        </motion.div>
      )}

      {/* Actions complétées */}
      {safeCompleted.length > 0 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45 }}
          className="px-4 py-3 rounded-2xl"
          style={{ background: `${theme.ink}03`, border: `1px solid ${theme.ink}06` }}>
          <p style={{ color: `${theme.ink}22`, fontSize: '8px', fontFamily: '"DM Mono", monospace', letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: '7px' }}>
            {isLiveMode ? 'Vos choix' : 'Actions réalisées'}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {safeCompleted.map(id => (
              <span key={id} className="px-2 py-0.5 rounded-md"
                style={{ background: `${theme.accent}10`, color: `${theme.accent}65`, fontSize: '8px', fontFamily: '"DM Mono", monospace', border: `1px solid ${theme.accent}15` }}>
                {id.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

// ─── Titre d'étape ────────────────────────────────────────────────────────────

const StepTitle = ({ step, phase, theme, isLiveMode }) => {
  const title = pick(isLiveMode ? step?.clientTitle : null, step?.title, '');
  if (!title) return null;
  return (
    <motion.div key={`${step?.id}-title`} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.2 }}
      className="px-4 py-3 rounded-2xl"
      style={{ background: theme.panelBg, border: `1px solid ${theme.panelBorder}` }}>
      {phase?.title && (
        <p style={{ color: `${theme.ink}20`, fontSize: '8px', fontFamily: '"DM Mono", monospace', letterSpacing: '0.25em', textTransform: 'uppercase', marginBottom: '3px' }}>
          {phase.title}
        </p>
      )}
      <h2 style={{ color: theme.ink, fontSize: '18px', fontWeight: 500, lineHeight: '1.3', fontFamily: '"DM Sans", sans-serif', letterSpacing: '-0.02em' }}>
        {title}
      </h2>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════

export const CommercialOverlay = ({ isLiveMode = false, theme, V, onAdvancePhase, postAction, commercialLiveGetDebriefData, onActiveMediaChange, onSnapshot }) => {
  const {
    engine,
    currentStep,
    currentStepIndex,
    scenario,
    currentPhase,
    phase:          globalPhase,
    applyAction,
    completedActions,
    unlockedActions,
  } = usePesa();

  const commercialLive = usePesa(s => s.commercialLive) ?? {
      enabled: false, token: null, role: null, connected: false,
      clientEvents: [], pendingClientEvents: [], compiledClientEvents: [],
      clientInputs: [], clientChoices: [],
  };

  const mode     = scenario?.displayMode === 'vente' ? 'vente' : 'formation';
  const [activeMedia,         setActiveMedia]         = useState(null);
  const [revealedDeltas,      setRevealedDeltas]      = useState(null);
  const [lastCompletedAction, setLastCompletedAction] = useState(null);
  const liveChannel = useRef(null);

  const displayTime    = engine?.state?.remainingTime ?? null;
  const isActionPhase  = currentPhase?.type === 'action';
  const isConsequences = currentPhase?.type === 'consequences';
  const isFinished     = globalPhase === 'finished';
  const totalSteps     = scenario?.steps?.length || 1;

  // ─── Actions disponibles ──────────────────────────────────────────────────
  const availableActions = useMemo(() => {
    if (currentPhase?.type !== 'action' || !currentPhase?.actionIds) return [];
    const safeUnlocked  = unlockedActions  instanceof Set ? unlockedActions  : new Set();
    const safeCompleted = completedActions instanceof Set ? completedActions : new Set();
    return currentPhase.actionIds
      .map(item => {
        const id     = typeof item === 'string' ? item : item.id;
        const action = currentStep?.actions?.find(a => a.id === id);
        // On inclut même les complétées pour que ActionCard puisse les afficher grisées
        if (!action || !safeUnlocked.has(id)) return null;
        return action;
      })
      .filter(Boolean);
  }, [currentPhase, currentStep, unlockedActions, completedActions]);

  const safeCompleted     = completedActions instanceof Set ? completedActions : new Set();
  const maxActions        = currentPhase?.constraint === 'max-actions-1' ? 1 : null;
  const actionsThisPhase  = (currentPhase?.actionIds || []).filter(id => safeCompleted.has(typeof id === 'string' ? id : id.id)).length;
  const isConstraintReached = maxActions !== null && actionsThisPhase >= maxActions;
  const keyActionId       = currentStep?.stepSuccessCriteria?.keyActionId;
  const keyActionDone     = keyActionId ? safeCompleted.has(keyActionId) : true;
  const feedbackIfMissed  = currentStep?.stepSuccessCriteria?.feedbackIfMissed;
  const isClientLive      = isLiveMode && commercialLive.role === 'client';
  

  // ─── Gestion des actions ──────────────────────────────────────────────────
  const handleAction = useCallback((action) => {
    applyAction(action.id);
    setLastCompletedAction(action.id);

    const normalizedMedia = action.media
      ? { type: action.media.type, url: action.media.url, title: action.label || action.media.title }
      : null;

    // Mettre à jour l'état local et le parent
    setActiveMedia(normalizedMedia);
    onActiveMediaChange?.(normalizedMedia);

    // Envoyer un snapshot immédiat avec ou sans média (un seul appel)
    if (normalizedMedia) {
      onSnapshot?.(normalizedMedia);   // envoie avec le média normalisé
    } else {
      onSnapshot?.();                  // envoie sans média
    }

    // Citation corpus (live et solo)
    const corpusQuote = findCorpusQuote(action.id, scenario);
    if (corpusQuote) {
      setTimeout(() => CorpusQuoteToast(corpusQuote, theme, isLiveMode), action.media ? 3500 : 1200);
    }

    // Réactions agents (solo uniquement)
    if (!isLiveMode) {
      const allAgents = [
        ...(engine?.state?.agents || []),
        ...(engine?.state?.families?.flatMap(f => f.agents || []) || []),
      ];
      const reacting = allAgents.find(a => {
        if (!a.reactionsTo) return false;
        return a.reactionsTo instanceof Map ? a.reactionsTo.has(action.id) : action.id in a.reactionsTo;
      });
      if (reacting) {
        const r = reacting.reactionsTo instanceof Map
          ? reacting.reactionsTo.get(action.id)
          : reacting.reactionsTo[action.id];
        const text = typeof r === 'object' ? r.solo : r;
        if (text && text !== '…') {
          setTimeout(() => AgentSoloToast(reacting, text, theme), 900);
        }
      }
    }

    if (liveChannel.current) {
      liveChannel.current.postMessage({
        type: 'action',
        actionId: action.id,
        timestamp: Date.now(),
      });
    }

    
    
  }, [applyAction, scenario, engine, isLiveMode, theme]);
  
  useEffect(() => {
    if (!currentPhase?.revealDelta) { setRevealedDeltas(null); return; }
    const init   = engine?.state?.initialParameters || {};
    const params = engine?.state?.parameters        || {};
    const d = {};
    Object.keys(params).forEach(k => {
      const before = init[k] ?? 50;
      const after  = params[k] ?? 50;
      d[k] = { before, after, delta: after - before };
    });
    setRevealedDeltas(d);
  }, [currentPhase?.revealDelta, engine?.state?.parameters]);

  

  // Reset média et last action au changement de phase
  useEffect(() => {
    setActiveMedia(null);
    setLastCompletedAction(null);
  }, [currentPhase]);

  if (!engine || !currentStep || !currentPhase) return null;

  return (
    <div
      className="relative w-full h-full flex flex-col pointer-events-none gap-3 overflow-y-auto"
      style={{ padding: '1.25rem 1.5rem', scrollbarWidth: 'none' }}
    >
      {/* HUD */}
      <div className="pointer-events-auto shrink-0">
        <HUD
          currentStepIndex={currentStepIndex}
          totalSteps={totalSteps}
          currentPhase={currentPhase}
          displayTime={displayTime}
          engine={engine}
          theme={theme}
          mode={mode}
          isLiveMode={isLiveMode}
        />
      </div>

      {/* Titre */}
      {!isFinished && (
        <div className="pointer-events-auto shrink-0">
          <StepTitle step={currentStep} phase={currentPhase} theme={theme} isLiveMode={isLiveMode} />
        </div>
      )}

      {/* Zone centrale */}
      <div className="flex-1 flex flex-col justify-center items-center max-w-2xl mx-auto w-full gap-3 z-10 relative pointer-events-auto">
        <AnimatePresence mode="wait">

          {/* Débrief */}
          {isFinished && (
            <motion.div key="debrief" className="w-full">
              <DebriefScene
                step={currentStep}
                scenario={scenario}
                completedActions={completedActions}
                theme={theme}
                isLiveMode={isLiveMode}
                V={V}
                commercialLiveGetDebriefData={commercialLiveGetDebriefData}
              />
            </motion.div>
          )}

          {/* Observation / tension / bifurcation */}
          {!isFinished && !isActionPhase && !isConsequences && (
            <NarrativeScene
              key={`narr-${currentPhase?.title}`}
              phase={currentPhase}
              step={currentStep}
              theme={theme}
              mode={mode}
              isLiveMode={isLiveMode}
              isConsequence={false}
            />
          )}

          {/* Action */}
          {!isFinished && isActionPhase && (
            <motion.div key="action" className="w-full">
              {/* Client live : afficher ClientActionScene */}
              {isClientLive && currentPhase?.clientActions ? (
                <ClientActionScene
                  clientActions={currentPhase.clientActions}
                  phaseKey={currentPhase.title || currentPhase.id || 'action'}
                  theme={theme}
                  postAction={postAction}
                />
              ) : (
                <ActionScene
                  phase={currentPhase}
                  step={currentStep}
                  availableActions={availableActions}
                  completedActions={completedActions}
                  isConstraintReached={isConstraintReached}
                  onAction={handleAction}
                  onAdvance={() => engine?.advancePhase()}
                  activeMedia={activeMedia}
                  onClearMedia={() => setActiveMedia(null)}
                  lastCompletedActionId={lastCompletedAction}
                  theme={theme}
                  mode={mode}
                  isLiveMode={isLiveMode}
                  V={V}
                  keyActionDone={keyActionDone}
                  feedbackIfMissed={feedbackIfMissed}
                  engine={engine}
                />
              )}
            </motion.div>
          )}

          {isLiveMode && commercialLive.role === 'seller' && <ClientActivity theme={theme} />}

          {/* Conséquences */}
          {!isFinished && isConsequences && (
            <motion.div key="cons" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="w-full space-y-3">
              <NarrativeScene
                phase={currentPhase}
                step={currentStep}
                theme={theme}
                mode={mode}
                isLiveMode={isLiveMode}
                isConsequence={true}
              />
              {revealedDeltas && (
                <ImpactReveal deltas={revealedDeltas} theme={theme} isLiveMode={isLiveMode} V={V} />
              )}
              <motion.button
                initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
                whileHover={{ y: -1 }} whileTap={{ scale: 0.989 }}
                onClick={() => onAdvancePhase ? onAdvancePhase() : engine?.advancePhase()}
                className="w-full py-3.5 rounded-2xl font-semibold flex items-center justify-center gap-3"
                style={{ background: theme.accent, color: '#fff', fontSize: '14px', fontFamily: '"DM Sans", sans-serif', letterSpacing: '-0.01em', boxShadow: `0 4px 18px ${theme.accent}28` }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
              >
                {pick(currentPhase?.advanceLabel, isLiveMode ? 'Étape suivante' : 'Suite')}
                <ArrowRight size={14} />
              </motion.button>
            </motion.div>
          )}

        </AnimatePresence>
      </div>
    </div>
  );
};

export default CommercialOverlay;